import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

export const AdBanner = ({ placement = "all", className = "" }) => {
  const { t } = useLang();
  const tracked = useRef(false);
  const { data: ad } = useQuery({
    queryKey: ["ad", placement],
    queryFn: async () => (await api.get("/ads", { params: { placement } })).data,
    staleTime: 60000,
  });

  useEffect(() => {
    if (ad && ad.id && !tracked.current) {
      tracked.current = true;
      api.post(`/ads/${ad.id}/impression`).catch(() => {});
    }
  }, [ad]);

  if (!ad || !ad.id) return null;

  const isInternal = ad.link_url?.startsWith("/");
  const onClick = () => { api.post(`/ads/${ad.id}/click`).catch(() => {}); };

  const inner = (
    <>
      <span className="absolute top-2 left-2 z-10 px-2 py-0.5 rounded-full text-[9px] font-mono uppercase tracking-wider bg-black/60 text-stone-400 border border-white/10">
        {t("ad_sponsored")}
      </span>
      <img src={ad.image_url} alt={ad.title} loading="lazy" className="w-full h-40 sm:h-44 object-cover transition-transform duration-500 group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0B0C0E]/90 via-[#0B0C0E]/40 to-transparent" />
      <div className="absolute inset-y-0 left-0 flex flex-col justify-center px-6 sm:px-8 max-w-[75%]">
        <div className="font-serif text-lg sm:text-2xl text-amber-100 leading-tight">{ad.title}</div>
        <span className="mt-2 inline-flex w-fit items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-amber-400 group-hover:text-amber-300 transition-colors">
          {t("ad_learn_more")} →
        </span>
      </div>
    </>
  );

  const cls = `group relative block overflow-hidden rounded-xl border border-amber-900/40 bg-[#121418] shadow-md hover:shadow-xl transition-shadow ${className}`;

  if (isInternal) {
    return (
      <Link to={ad.link_url} onClick={onClick} data-testid={`ad-banner-${placement}`} className={cls}>
        {inner}
      </Link>
    );
  }

  return (
    <a href={ad.link_url} target="_blank" rel="noopener noreferrer sponsored" onClick={onClick} data-testid={`ad-banner-${placement}`} className={cls}>
      {inner}
    </a>
  );
};

export default AdBanner;
